/**
 * Open an OAuth authorize URL in a centered popup and resolve when the
 * backend callback page posts a message back. The wizard uses the result
 * to tell the user whether the popup finished, was closed, denied, timed
 * out, or never opened.
 *
 * The callback page (served by backend) must post a message shaped like:
 *   { source: 'scriptix-oauth', attempt_id, ok, code?, reason? }
 */

import type { OAuthResult } from '@utils/types/connections/connection.ts';

export interface OAuthPopupMessage {
  source: 'scriptix-oauth';
  attempt_id: string;
  ok: boolean;
  code?: string;
  reason?: 'denied' | 'cancelled';
}

export interface OAuthPopupOptions {
  url: string;
  attemptId: string;
  /** Origin the callback page is served from. Defaults to window.location.origin. */
  expectedOrigin?: string;
  /** Milliseconds before the attempt is considered expired. Default 5 minutes. */
  timeoutMs?: number;
  width?: number;
  height?: number;
}

const DEFAULT_TIMEOUT_MS = 5 * 60 * 1000;

function centeredFeatures(width: number, height: number): string {
  const left =
    typeof window !== 'undefined' && window.screen
      ? Math.max(0, (window.screen.width - width) / 2)
      : 0;
  const top =
    typeof window !== 'undefined' && window.screen
      ? Math.max(0, (window.screen.height - height) / 2)
      : 0;
  return [
    `width=${width}`,
    `height=${height}`,
    `left=${Math.round(left)}`,
    `top=${Math.round(top)}`,
    'resizable=yes',
    'scrollbars=yes'
  ].join(',');
}

function isPopupMessage(
  data: unknown,
  attemptId: string
): data is OAuthPopupMessage {
  if (!data || typeof data !== 'object') return false;
  const m = data as Partial<OAuthPopupMessage>;
  return m.source === 'scriptix-oauth' && m.attempt_id === attemptId;
}

/**
 * Launch the popup and resolve with the attempt outcome. The caller is
 * expected to render retry UI on any `ok: false` result; this function
 * never throws.
 */
export function launchOAuthPopup(
  options: OAuthPopupOptions
): Promise<OAuthResult> {
  const {
    url,
    attemptId,
    expectedOrigin = typeof window !== 'undefined'
      ? window.location.origin
      : '',
    timeoutMs = DEFAULT_TIMEOUT_MS,
    width = 560,
    height = 720
  } = options;

  return new Promise<OAuthResult>(resolve => {
    let popup: Window | null = null;
    let closedPoll: ReturnType<typeof setInterval> | null = null;
    let timeout: ReturnType<typeof setTimeout> | null = null;
    let settled = false;

    const cleanup = () => {
      if (closedPoll) {
        clearInterval(closedPoll);
        closedPoll = null;
      }
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      window.removeEventListener('message', onMessage);
      if (popup && !popup.closed) {
        try {
          popup.close();
        } catch {
          // Popup may already be unreachable (CORS, closed by user).
        }
      }
    };

    const settle = (result: OAuthResult) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(result);
    };

    const onMessage = (event: MessageEvent) => {
      // Origin check: ignore anything not from the expected origin so a
      // page in another tab cannot forge an authorization code.
      if (expectedOrigin && event.origin !== expectedOrigin) return;
      if (!isPopupMessage(event.data, attemptId)) return;

      const payload = event.data;
      if (payload.ok && payload.code) {
        settle({
          ok: true,
          code: payload.code,
          attempt_id: payload.attempt_id
        });
      } else {
        settle({
          ok: false,
          attempt_id: payload.attempt_id,
          reason: payload.reason === 'denied' ? 'denied' : 'cancelled'
        });
      }
    };

    window.addEventListener('message', onMessage);

    try {
      popup = window.open(
        url,
        'scriptix-oauth',
        centeredFeatures(width, height)
      );
    } catch {
      popup = null;
    }

    if (!popup || popup.closed || typeof popup.focus !== 'function') {
      settle({ ok: false, reason: 'popup_blocked' });
      return;
    }

    try {
      popup.focus();
    } catch {
      // Focus is best-effort; some browsers block it when backgrounded.
    }

    closedPoll = setInterval(() => {
      if (popup && popup.closed) {
        settle({ ok: false, reason: 'cancelled' });
      }
    }, 400);

    timeout = setTimeout(() => {
      settle({ ok: false, reason: 'expired' });
    }, timeoutMs);
  });
}
